import type { GetTasksResponse } from '../schema/GetTasksServiceResponse';
import type { GetTasksRequestParams } from '../schema/request/GetTasksRequestParams';
import { isGetFolderTasksRequestParams } from '../utils/typeGuard';
import { buildGetTasksUrl } from '../utils/buildGetTasksUrl';
import { makeTodoGetRequest } from '../utils/makeTodoGetRequest';
import { hasTeamsSource } from '../utils/hasTeamsSource';

/**
 * Makes the Get tasks call to the server to get task items specified by the parameters
 *
 * @param requestParams get task request parameters used to build the tasks request
 * @returns promise of GetTasksServiceResponse
 */
export default async function getTasksService(
    requestParams: GetTasksRequestParams
): Promise<GetTasksResponse> {
    const actionName = 'GET_TASKS_IN_FOLDER';
    let taskResponse = await makeTodoGetRequest(buildGetTasksUrl(requestParams), actionName);
    if (taskResponse.errorResponse && isGetFolderTasksRequestParams(requestParams)) {
        /** TODO VSO 38608: this retry logic is needed when the user is a notes user, because of recent regression
         * To mitigate this bug, on the retry we request tasks with status eq 'NotStarted' (instead of status ne 'Completed').
         */
        const retryActionName = 'GET_TASKS_IN_FOLDER_RETRY';
        if (isStatusNeCompleted(requestParams)) {
            requestParams.filters.taskStatusFilters = [
                {
                    status: 'NotStarted',
                    equalityOperator: 'eq',
                },
                {
                    status: 'InProgress',
                    equalityOperator: 'eq',
                },
                {
                    status: 'Deferred',
                    equalityOperator: 'eq',
                },
                {
                    status: 'WaitingOnOthers',
                    equalityOperator: 'eq',
                },
            ];
        }
        taskResponse = await makeTodoGetRequest(buildGetTasksUrl(requestParams), retryActionName);
    }
    taskResponse.Value = taskResponse.Value.filter(task => !hasTeamsSource(task));
    return taskResponse;
}

function isStatusNeCompleted(requestParams: GetTasksRequestParams): boolean {
    if (requestParams.filters.taskStatusFilters?.length === 1) {
        const filter = requestParams.filters.taskStatusFilters[0];
        if (filter.status === 'Completed' && filter.equalityOperator === 'ne') {
            return true;
        }
    }
    return false;
}
